import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Row, Col, Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Axios from 'axios';
import i18next from 'i18next';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ConsultaAPI } from '../../../../shared/http-requests';
import { useSearchContext } from '../../../../shared/contexts/SearchContextProvider';
import { useAccessibilityContext } from '../../../consulta-acessibilidade/contexts/AccessibilityContext';
import { useLocation } from 'react-router-dom';
import { ToastifyHelper } from '../../../../shared/helpers';
import shortid from 'shortid';

function padArray(arr, size, fillValue) {
    if (!Array.isArray(arr)) {
        throw new Error('O argumento "arr" deve ser um array.');
    }

    const oldArray = [...JSON.parse(JSON.stringify(arr))];
    const newArr = [];

    for (let i = 0; i < size; i++) {
        if (oldArray.length - 1 >= i) {
            newArr.push(oldArray[i]);
        } else {
            newArr.push(JSON.parse(JSON.stringify(fillValue)));
        }
    }

    return newArr;
}

const isValidComparacao = value => ['=', '>', '<', 'entre'].includes(value);

const MAX_VISIBLE_TYPE_LINES = 6;
const TYPE_COLUMNS = 3;
const MAX_VISIBLE_TYPES = MAX_VISIBLE_TYPE_LINES * TYPE_COLUMNS;

const chunkIntoColumns = (items, numberOfColumns) => {
    if (!Array.isArray(items) || items.length === 0) {
        return Array.from({ length: numberOfColumns }, () => []);
    }

    const itemsPerColumn = Math.ceil(items.length / numberOfColumns);

    return Array.from({ length: numberOfColumns }, (_, colIndex) =>
        items.slice(colIndex * itemsPerColumn, (colIndex + 1) * itemsPerColumn),
    );
};

const safeDecodeURIComponent = value => {
    if (!value || typeof value !== 'string') {
        return '';
    }

    let decodedValue = value;

    try {
        let previousValue = '';
        while (decodedValue !== previousValue) {
            previousValue = decodedValue;
            decodedValue = decodeURIComponent(decodedValue);
        }
    } catch (error) {
        return value;
    }

    return decodedValue;
};

const normalizeValue = value => String(value ?? '').trim();

const parseTipoFromUrl = codTipoObra => {
    if (!codTipoObra || typeof codTipoObra !== 'string') {
        return [];
    }

    const decodedValue = safeDecodeURIComponent(codTipoObra);

    return [
        ...new Set(
            decodedValue
                .split(',')
                .map(item => normalizeValue(item))
                .filter(Boolean),
        ),
    ];
};

const buildTipoValueForRequest = tiposSelecionados => {
    if (!Array.isArray(tiposSelecionados) || tiposSelecionados.length === 0) {
        return '';
    }

    return `${tiposSelecionados.join(',')},`;
};

const buildTipoValueForUrl = tiposSelecionados => {
    if (!Array.isArray(tiposSelecionados) || tiposSelecionados.length === 0) {
        return '';
    }

    return tiposSelecionados.join(',');
};

const getDefaultOptionByIndex = (opcoes, index) => {
    if (!Array.isArray(opcoes) || opcoes.length === 0) {
        return 'obra';
    }

    return opcoes[index]?.codigo || opcoes[0]?.codigo || 'obra';
};

const buildSearchOptionsFromUrl = (search, opcoes) => {
    const params = new URLSearchParams(search);

    const fields = [
        {
            fieldIndex: 1,
            option: params.get('for') || getDefaultOptionByIndex(opcoes, 0),
            termo: params.get('q') || '',
            condition: params.get('condition') || '-1',
        },
        {
            fieldIndex: 2,
            option: params.get('for2') || getDefaultOptionByIndex(opcoes, 1),
            termo: params.get('q2') || '',
            condition: params.get('condition2') || '-1',
        },
        {
            fieldIndex: 3,
            option: params.get('for3') || getDefaultOptionByIndex(opcoes, 2),
            termo: params.get('q3') || '',
            condition: '-1',
        },
    ];

    if (!fields[0].condition || fields[0].condition === '') {
        fields[0].condition = '-1';
    }

    if (!fields[1].condition || fields[1].condition === '') {
        fields[1].condition = '-1';
    }

    if (fields[0].condition === '-1') {
        fields[1].termo = fields[1].termo || '';
        fields[1].condition = '-1';
        fields[2].termo = fields[2].termo || '';
    }

    if (fields[1].condition === '-1') {
        fields[2].termo = fields[2].termo || '';
    }

    if (['AFT', 'BEF'].includes(fields[0].condition)) {
        fields[1].option = fields[0].option;
        fields[1].condition = '-1';
        fields[2].termo = '';
    }

    return fields;
};

const buildSearchOptionsFromProps = (opcoesConsulta, opcoes) => {
    if (opcoesConsulta?.length > 0 && opcoes?.length > 0) {
        return padArray(opcoesConsulta, 3, {
            option: null,
            termo: '',
            condition: '-1',
        }).map((x, i) => {
            const item = { ...x };

            item.fieldIndex = i + 1;

            if (item.option === null || item.option === undefined || item.option === '') {
                item.option = getDefaultOptionByIndex(opcoes, i);
            }

            if (!item.condition) {
                item.condition = '-1';
            }

            return item;
        });
    }

    return [
        {
            fieldIndex: 1,
            option: getDefaultOptionByIndex(opcoes, 0),
            termo: '',
            condition: '-1',
        },
        {
            fieldIndex: 2,
            option: getDefaultOptionByIndex(opcoes, 1),
            termo: '',
            condition: '-1',
        },
        {
            fieldIndex: 3,
            option: getDefaultOptionByIndex(opcoes, 2),
            termo: '',
            condition: '-1',
        },
    ];
};

const getTipoTotal = item => {
    const total = item?.total;

    if (total === null || total === undefined || total === '') {
        return 0;
    }

    const totalNumero = Number(total);

    if (Number.isNaN(totalNumero)) {
        return 0;
    }

    return totalNumero;
};

const formatTipoLabel = item => {
    const descricao = item?.desc_tipo_obra || '';
    const total = getTipoTotal(item);

    return `${descricao} (${total})`;
};

const PesquisaJurisprudencia = props => {
    const { t } = useTranslation();
    const [opcoes, setOpcoes] = useState([]);
    const { opcoesConsulta } = props;
    const [searching, setSearching] = useState(false);
    const [typeOfAtos, setTypeOfAtos] = useState(null);
    const [comparacao, setComparacao] = useState('-1');
    const [dataInicial, setDataInicial] = useState('');
    const [dataFinal, setDataFinal] = useState('');
    const [tiposSelecionados, setTiposSelecionados] = useState([]);
    const [permissionFilters, setPermissionFilters] = useState(false);
    const [showAllTipos, setShowAllTipos] = useState(false);
    const autoSearchCancelTokenSrc = useRef();
    const hasHydratedFromUrlRef = useRef(false);

    const location = useLocation();
    const { search } = location;

    const [initialDataFetched, setInitialDataFetched] = useState(false);

    const { search: searchFromContext, loadingSearch } = useSearchContext();
    const { searchInputRef } = useAccessibilityContext();

    const [localOpcoesConsulta, setLocalOpcoesConsulta] = useState([
        {
            fieldIndex: 1,
            option: null,
            termo: '',
            condition: '-1',
        },
        {
            fieldIndex: 2,
            option: null,
            termo: '',
            condition: '-1',
        },
        {
            fieldIndex: 3,
            option: null,
            termo: '',
            condition: '-1',
        },
    ]);

    const getCurrentSearch = useCallback(() => {
        if (typeof window !== 'undefined' && typeof window.location?.search === 'string') {
            return window.location.search || '';
        }

        return search || '';
    }, [search]);

    const syncFilterStateFromSearch = useCallback((searchStringParam = null) => {
        const searchString = searchStringParam ?? getCurrentSearch();
        const urlSearchParams = new URLSearchParams(searchString);
        const comp = urlSearchParams.get('comparacao');
        const dtInicial = urlSearchParams.get('data_inicial');
        const dtFinal = urlSearchParams.get('data_final');
        const codTipoObra = urlSearchParams.get('cod_tipo_obra');

        const comparacaoValida = isValidComparacao(comp);

        setComparacao(comparacaoValida ? comp : '-1');
        setDataInicial(comparacaoValida ? dtInicial || '' : '');
        setDataFinal(comp === 'entre' ? dtFinal || '' : '');
        setTiposSelecionados(parseTipoFromUrl(codTipoObra));
    }, [getCurrentSearch]);

    useEffect(() => {
        syncFilterStateFromSearch();
    }, [search, syncFilterStateFromSearch]);

    useEffect(() => {
        const cancelTokenSrc = Axios.CancelToken.source();

        ConsultaAPI.getPermissionFiltersAtosAndJurisprudencia(cancelTokenSrc.token)
            .then(response => {
                const { data } = response;
                if (data) {
                    const { permission } = data;
                    setPermissionFilters(permission);

                    if (permission) {
                        ConsultaAPI.getTypeOptionsJurisprudenciaSearch(cancelTokenSrc.token)
                            .then(response => {
                                const { data } = response;
                                if (data) {
                                    setTypeOfAtos(data);
                                }
                            })
                            .catch(() => {});
                    }
                }
            })
            .catch(error => {
                if (!Axios.isCancel(error)) {
                    console.error(error);
                }
            });

        return () => {
            cancelTokenSrc.cancel();
        };
    }, []);

    useEffect(() => {
        autoSearchCancelTokenSrc.current = Axios.CancelToken.source();

        if (
            opcoesConsulta &&
            opcoesConsulta.length === 1 &&
            opcoesConsulta[0].option === null &&
            opcoesConsulta[0].termo?.trim() === ''
        ) {
            ConsultaAPI.legislacaoAutoSearchPermission(
                autoSearchCancelTokenSrc.current.token,
            )
                .then(response => {
                    if (response) {
                        const { data } = response;

                        if (data && data?.permission === true) {
                            if (!initialDataFetched) {
                                ConsultaAPI.buscarConsulta(
                                    'jurisprudencia',
                                    {
                                        tipo_termos: 'U',
                                        coluna_um: '-1',
                                        search_id: shortid(),
                                        search_tab: 'jurisprudencia',
                                        termo_pesquisa: '-1',
                                        last_added: true,
                                    },
                                    '/jurisprudencia',
                                    undefined,
                                    undefined,
                                    autoSearchCancelTokenSrc.current.token,
                                );
                            }
                            setInitialDataFetched(true);
                        }
                    }
                })
                .catch(e => {
                    if (!Axios.isCancel(e)) {
                        console.log(e);
                    }
                });
        }

        return () => autoSearchCancelTokenSrc.current.cancel();
    }, [opcoesConsulta, loadingSearch, initialDataFetched]);

    const condicoes = [
        { label: t('AND'), value: 'AND', intermediario: false },
        { label: t('OR'), value: 'OR', intermediario: false },
        { label: t('NOT'), value: 'NOT', intermediario: false },
        { label: t('AFT'), value: 'AFT', intermediario: true },
        { label: t('BEF'), value: 'BEF', intermediario: true },
    ];

    useEffect(() => {
        const cancelToken = Axios.CancelToken.source();

        ConsultaAPI.getOptionsJurisprudencia(cancelToken.token)
            .then(response => {
                setOpcoes(response.data);
                response.data.forEach(d => {
                    i18next.addResource('pt', 'translation', d.codigo, d.descricao);
                    i18next.addResource('en', 'translation', d.codigo, d.descricao);
                    i18next.addResource('es', 'translation', d.codigo, d.descricao);
                });
            })
            .catch(error => {
                if (!Axios.isCancel(error)) return;
            });

        return () => {
            cancelToken.cancel();
        };
    }, []);

    useEffect(() => {
        if (opcoes.length === 0) {
            return;
        }

        const currentSearch = getCurrentSearch();
        const params = new URLSearchParams(currentSearch);

        const urlHasAnyRelevantParam = [
            'for',
            'for2',
            'for3',
            'q',
            'q2',
            'q3',
            'condition',
            'condition2',
            'comparacao',
            'data_inicial',
            'data_final',
            'cod_tipo_obra',
        ].some(param => {
            const value = params.get(param);
            return value !== null && String(value).trim() !== '';
        });

        if (urlHasAnyRelevantParam) {
            setLocalOpcoesConsulta(buildSearchOptionsFromUrl(currentSearch, opcoes));
            hasHydratedFromUrlRef.current = true;
            return;
        }

        if (!hasHydratedFromUrlRef.current) {
            setLocalOpcoesConsulta(buildSearchOptionsFromProps(opcoesConsulta, opcoes));
        }
    }, [search, opcoes, opcoesConsulta, initialDataFetched, getCurrentSearch]);

    const tiposVisiveis = useMemo(() => {
        if (!Array.isArray(typeOfAtos)) return [];
        return showAllTipos ? typeOfAtos : typeOfAtos.slice(0, MAX_VISIBLE_TYPES);
    }, [typeOfAtos, showAllTipos]);

    const tiposEmColunas = useMemo(
        () => chunkIntoColumns(tiposVisiveis, TYPE_COLUMNS),
        [tiposVisiveis],
    );

    const shouldShowToggleTipos =
        Array.isArray(typeOfAtos) && typeOfAtos.length > MAX_VISIBLE_TYPES;

    const handleToggleTipo = codTipoObra => {
        const value = normalizeValue(codTipoObra);

        setTiposSelecionados(prevState => {
            if (prevState.includes(value)) {
                return prevState.filter(item => item !== value);
            }

            return [...prevState, value];
        });
    };

    const handleChangeComboTermo = (e, index) => {
        e.preventDefault();
        const valor = e.target.value;
        const optPesquisa = JSON.parse(JSON.stringify(localOpcoesConsulta));

        optPesquisa[index].option = valor;

        if (['AFT', 'BEF'].includes(optPesquisa[index].condition) && optPesquisa[index + 1]) {
            optPesquisa[index + 1].option = valor;
        }

        setLocalOpcoesConsulta(optPesquisa);
    };

    const handleChangeTextTermo = (e, index) => {
        e.preventDefault();
        const valor = e.target.value;
        const optPesquisa = JSON.parse(JSON.stringify(localOpcoesConsulta));
        optPesquisa[index].termo = valor;
        setLocalOpcoesConsulta(optPesquisa);
    };

    const handleChangeComboCondicao = (e, index) => {
        e.preventDefault();
        const valor = e.target.value;
        const optPesquisa = JSON.parse(JSON.stringify(localOpcoesConsulta));
        optPesquisa[index].condition = valor;

        if (index === 0 && valor === '-1') {
            if (optPesquisa[1].termo?.trim() !== '') {
                optPesquisa[1].termo = '';
            }

            if (
                optPesquisa[1].condition !== undefined &&
                optPesquisa[1].condition !== '-1'
            ) {
                optPesquisa[1].condition = '-1';
            }

            if (optPesquisa[2].termo?.trim() !== '') {
                optPesquisa[2].termo = '';
            }
        } else if (
            index === 1 &&
            valor === '-1' &&
            optPesquisa[2].termo?.trim() !== ''
        ) {
            optPesquisa[2].termo = '';
        } else if (['AFT', 'BEF'].includes(optPesquisa[0].condition)) {
            optPesquisa[2].termo = '';
            optPesquisa[1].condition = '-1';
            optPesquisa[1].option = optPesquisa[0].option;
        }

        setLocalOpcoesConsulta(optPesquisa);
    };

    const getCondicoes = indexLinha => {
        if (indexLinha === 0) {
            return condicoes.map(condicao => (
                <option key={`cond-${condicao.value}`} value={condicao.value}>
                    {condicao.label}
                </option>
            ));
        }

        return condicoes
            .filter(c => !c.intermediario)
            .map(condicao => (
                <option key={`cond-${condicao.value}`} value={condicao.value}>
                    {condicao.label}
                </option>
            ));
    };

    const buildVisibleQueryParams = params => {
        const cleaned = {};

        Object.keys(params).forEach(key => {
            const value = params[key];

            if (
                value !== null &&
                value !== undefined &&
                value !== '' &&
                value !== '-1'
            ) {
                cleaned[key] = value;
            }
        });

        return cleaned;
    };

    const replaceBrowserUrl = params => {
        const visibleParams = buildVisibleQueryParams(params);
        const searchParams = new URLSearchParams(visibleParams).toString();
        const newUrl = `${location.pathname}${searchParams ? `?${searchParams}` : ''}`;

        window.history.replaceState({}, '', newUrl);
        syncFilterStateFromSearch(window.location.search);
    };

    const localValidate = () => {
        const currentLocalOpcoesConsulta = JSON.parse(
            JSON.stringify(localOpcoesConsulta),
        ).map((x, i) => {
            x.fieldIndex = i + 1;
            x.error = false;
            return x;
        });

        const hasTermos = currentLocalOpcoesConsulta.some(
            x => x.termo && x.termo.trim() !== '',
        );

        const hasTipo = tiposSelecionados.length > 0;
        const hasDataInicial = !!String(dataInicial || '').trim();
        const hasDataFinal = !!String(dataFinal || '').trim();
        const filtroAnoAtivo = isValidComparacao(comparacao);
        const hasFiltroAno = filtroAnoAtivo && hasDataInicial;

        if (hasTermos) {
            currentLocalOpcoesConsulta.forEach(x => {
                if (x.fieldIndex === 1) {
                    if (x.termo.trim() === '') {
                        x.error = true;
                    }
                }

                if (x.fieldIndex === 2) {
                    if (
                        x.termo?.trim() === '' &&
                        currentLocalOpcoesConsulta[0].condition !== undefined &&
                        currentLocalOpcoesConsulta[0].condition !== '-1'
                    ) {
                        x.error = true;
                    }
                }

                if (x.fieldIndex === 3) {
                    if (
                        x.termo?.trim() === '' &&
                        currentLocalOpcoesConsulta[1].condition !== undefined &&
                        currentLocalOpcoesConsulta[1].condition !== '-1'
                    ) {
                        x.error = true;
                    }
                }
            });
        }

        const errors = [];

        if (!filtroAnoAtivo && (hasDataInicial || hasDataFinal)) {
            errors.push(
                `${t('EnecessarioInformarOCampo')} '${t('Condicao')}'`,
            );
        }

        if (filtroAnoAtivo) {
            if (!hasDataInicial) {
                errors.push(
                    `${t('EnecessarioInformarOCampo')} '${t('AnoPublicacao')}'`,
                );
            }

            if (comparacao === 'entre') {
                if (!hasDataFinal) {
                    errors.push(
                        `${t('EnecessarioInformarOCampo')} '${t(
                            'AnoPublicacaoFinal',
                        )}'`,
                    );
                } else if (hasDataInicial && Number(dataFinal) < Number(dataInicial)) {
                    errors.push(
                        `O campo '${t(
                            'AnoPublicacaoFinal',
                        )}' não pode ser menor que '${t('AnoPublicacao')}'`,
                    );
                }
            }
        }

        const hasAlgumFiltroValido =
            hasTermos ||
            hasTipo ||
            hasFiltroAno ||
            (filtroAnoAtivo && comparacao === 'entre' && hasDataFinal);

        if (!hasAlgumFiltroValido && !hasDataInicial && !hasDataFinal) {
            errors.push(t('InformeAoMenosUmCampo'));
        }

        if (errors.length > 0) {
            errors.forEach(error => {
                ToastifyHelper.warning(error);
            });
            return false;
        }

        setLocalOpcoesConsulta(currentLocalOpcoesConsulta);
        return currentLocalOpcoesConsulta.every(x => !x.error);
    };

    const handleClickSearch = e => {
        e.preventDefault();

        const filtroAnoAtivo = isValidComparacao(comparacao);

        if (!localValidate()) {
            return;
        }

        if (autoSearchCancelTokenSrc.current) {
            autoSearchCancelTokenSrc.current?.cancel();
        }

        const currentLocalOpcoesConsulta = JSON.parse(
            JSON.stringify(localOpcoesConsulta),
        ).filter(x => x.termo?.trim() !== '');

        const map = ['um', 'dois', 'tres'];
        const data = {
            cod_tipo_obra: '',
            comparacao: '',
            data_inicial: '',
            data_final: '',
        };
        const queryParams = {
            cod_tipo_obra: '',
            comparacao: '',
            data_inicial: '',
            data_final: '',
        };

        currentLocalOpcoesConsulta.forEach((item, index) => {
            data[`coluna_${map[index]}`] = item.option;
            queryParams[`for${index === 0 ? '' : index + 1}`] = item.option;

            if (index === 0) {
                queryParams.q = item.termo;
                data.termo_pesquisa = item.termo;
            } else {
                queryParams[`q${index + 1}`] = item.termo;
                data[`termo_pesquisa_${map[index]}`] = item.termo;
            }

            if (item?.condition && item.condition !== '-1') {
                data[`condicao_${map[index]}`] = item.condition;
                queryParams[`condition${index === 0 ? '' : index + 1}`] =
                    item.condition;
            }
        });

        const codTipoObraValueForRequest = buildTipoValueForRequest(tiposSelecionados);
        const codTipoObraValueForUrl = buildTipoValueForUrl(tiposSelecionados);

        data.cod_tipo_obra = codTipoObraValueForRequest || '';
        queryParams.cod_tipo_obra = codTipoObraValueForUrl || '';

        if (filtroAnoAtivo) {
            data.comparacao = comparacao;
            queryParams.comparacao = comparacao;

            data.data_inicial = String(dataInicial || '').trim();
            queryParams.data_inicial = String(dataInicial || '').trim();

            if (comparacao === 'entre') {
                data.data_final = String(dataFinal || '').trim();
                queryParams.data_final = String(dataFinal || '').trim();
            } else {
                data.data_final = '';
                queryParams.data_final = '';
            }
        } else {
            data.comparacao = '';
            data.data_inicial = '';
            data.data_final = '';
            queryParams.comparacao = '';
            queryParams.data_inicial = '';
            queryParams.data_final = '';
        }

        const cleanedQueryParams = buildVisibleQueryParams(queryParams);

        replaceBrowserUrl(cleanedQueryParams);
        setLocalOpcoesConsulta(buildSearchOptionsFromUrl(window.location.search, opcoes));
        searchFromContext(data, cleanedQueryParams);

        setTimeout(() => {
            replaceBrowserUrl(cleanedQueryParams);
            setLocalOpcoesConsulta(buildSearchOptionsFromUrl(window.location.search, opcoes));
            syncFilterStateFromSearch(window.location.search);
        }, 0);
    };

    const getRuleCondition = (index, opcao) => {
        if (searching) {
            return ![undefined, '-1'].includes(opcao.condition) && index < 2;
        }
        return index < 2;
    };

    let fieldContainerStyles = {};

    if (searching) {
        fieldContainerStyles = {
            visibility: searching ? 'hidden' : 'visible',
            height: searching ? '0px' : 'auto',
            width: searching ? '0px' : 'auto',
            margin: searching ? '0px' : 'auto',
            padding: searching ? '0px' : 'auto',
        };
    }

    const handleOnChangeCondition = e => {
        const { value } = e.target;
        const comparacaoValida = isValidComparacao(value);
        const currentParams = Object.fromEntries(new URLSearchParams(getCurrentSearch()).entries());

        setComparacao(comparacaoValida ? value : '-1');

        if (!comparacaoValida) {
            setDataInicial('');
            setDataFinal('');

            replaceBrowserUrl({
                ...currentParams,
                comparacao: '',
                data_inicial: '',
                data_final: '',
            });

            return;
        }

        if (value !== 'entre') {
            setDataFinal('');

            replaceBrowserUrl({
                ...currentParams,
                comparacao: value,
                data_final: '',
            });
        } else {
            replaceBrowserUrl({
                ...currentParams,
                comparacao: value,
            });
        }
    };

    return (
        <Row className="content__left__input px-3">
            {searching ? (
                <Col md="10" xs="12" className="p-4">
                    <div
                        className="d-flex align-items-center"
                        style={{
                            gap: '16px',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <FontAwesomeIcon size="1x" icon={faSpinner} spin />
                        <span className="cortxt">{t('RealizandoConsulta')}</span>
                    </div>
                </Col>
            ) : (
                ''
            )}

            <Col style={fieldContainerStyles} md="10" xs="12">
                {permissionFilters === true && Array.isArray(typeOfAtos) && typeOfAtos.length > 0 ? (
                    <Row className="mt-3 mb-2">
                        <Col xs="12" className="input-holder">
                            <div className="rotulo">{t('Tipo')}</div>

                            <div
                                style={{
                                    width: '100%',
                                    border: '1px solid #e3e3e3',
                                    borderRadius: '8px',
                                    padding: '16px',
                                }}
                            >
                                <Row>
                                    {tiposEmColunas.map((coluna, columnIndex) => (
                                        <Col
                                            key={`coluna-tipos-${columnIndex}`}
                                            md="4"
                                            sm="6"
                                            xs="12"
                                        >
                                            {coluna.map(item => {
                                                const checked = tiposSelecionados.includes(
                                                    normalizeValue(item.cod_tipo_obra),
                                                );

                                                return (
                                                    <label
                                                        key={`tipo-${item.cod_tipo_obra}`}
                                                        htmlFor={`tipo-${item.cod_tipo_obra}`}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: '10px',
                                                            marginBottom: '10px',
                                                            cursor: 'pointer',
                                                            color: '#2f4154',
                                                            fontWeight: 400,
                                                        }}
                                                    >
                                                        <input
                                                            id={`tipo-${item.cod_tipo_obra}`}
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() =>
                                                                handleToggleTipo(item.cod_tipo_obra)
                                                            }
                                                            style={{
                                                                marginTop: '3px',
                                                                minWidth: '16px',
                                                            }}
                                                        />
                                                        <span>{formatTipoLabel(item)}</span>
                                                    </label>
                                                );
                                            })}
                                        </Col>
                                    ))}
                                </Row>
                                {shouldShowToggleTipos && (
                                    <button
                                        type="button"
                                        onClick={() => setShowAllTipos(prev => !prev)}
                                        className="b-none bkzero click d-inline f400 hover htema link pad5 rad5 s13"
                                    >
                                        {showAllTipos ? (
                                            <>
                                                <FontAwesomeIcon icon="minus" className="r5" />
                                                <span>{t('MostrarMenos')}...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon="plus" className="r5" />
                                                <span>{t('MostrarMais')}...</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : null}

                {localOpcoesConsulta &&
                    localOpcoesConsulta.map((opcao, index) => (
                        <Row
                            className={`w-100 ${
                                index !== 0 &&
                                'additional_search_input pl-sm-0 pl-2 pr-sm-0 pr-2'
                            }`}
                            key={`row-termo-${index}`}
                        >
                            <Col
                                sm="6"
                                md="3"
                                xs="12"
                                className="input-holder mt-3"
                            >
                                <div className="rotulo">{t('PesquisarPor')}</div>
                                <Input
                                    type="select"
                                    name={`for${index === 0 ? '' : index + 1}`}
                                    value={opcao.option ?? ''}
                                    onChange={e => handleChangeComboTermo(e, index)}
                                    disabled={
                                        (index > 0 &&
                                            localOpcoesConsulta[index - 1]?.condition === undefined) ||
                                        localOpcoesConsulta[index - 1]?.condition === '-1'
                                    }
                                    readOnly={
                                        index > 0 &&
                                        ['AFT', 'BEF'].includes(localOpcoesConsulta[0].condition)
                                    }
                                >
                                    {opcoes
                                        ?.filter(x => {
                                            return !localOpcoesConsulta?.some(
                                                (loc, i) =>
                                                    x.codigo?.toString()?.trim()?.toLowerCase() ===
                                                        'livre' &&
                                                    x.codigo === loc.option &&
                                                    i !== index,
                                            );
                                        })
                                        ?.map(termo => (
                                            <option
                                                key={`opt-arquivo-${termo.codigo}`}
                                                value={termo.codigo}
                                            >
                                                {termo.descricao}
                                            </option>
                                        ))}
                                </Input>
                                <div className="invalid-feedback">
                                    {t('CampoBuscaObrigatorio')}
                                </div>
                            </Col>

                            <Col sm="6" xs="12" className="input-holder mt-3">
                                <div className="rotulo">{t('CampoBusca')}</div>
                                <Input
                                    innerRef={r => {
                                        if (index === 0) {
                                            searchInputRef.current = r;
                                        }
                                    }}
                                    type="text"
                                    name={`q${index === 0 ? '' : index + 1}`}
                                    placeholder={t('DigiteSuaBuscaAqui')}
                                    className="foco rad10"
                                    disabled={
                                        (index === 2 &&
                                            ['AFT', 'BEF'].includes(localOpcoesConsulta[0].condition)) ||
                                        (index > 0 &&
                                            localOpcoesConsulta[index - 1]?.condition === undefined) ||
                                        localOpcoesConsulta[index - 1]?.condition === '-1'
                                    }
                                    invalid={opcao?.error}
                                    value={opcao.termo || ''}
                                    onChange={e => handleChangeTextTermo(e, index)}
                                />
                                <div className="invalid-feedback">
                                    {t('CampoBuscaObrigatorio')}
                                </div>
                            </Col>

                            {getRuleCondition(index, opcao) ? (
                                <Col
                                    xs="12"
                                    sm="6"
                                    md="2"
                                    className="mt-3 input-holder"
                                >
                                    <div className="rotulo">{t('Condicionais')}</div>
                                    <Input
                                        type="select"
                                        disabled={
                                            (index === 1 &&
                                                ['AFT', 'BEF'].includes(localOpcoesConsulta[0].condition)) ||
                                            (index > 0 &&
                                                localOpcoesConsulta[index - 1]?.condition === undefined) ||
                                            localOpcoesConsulta[index - 1]?.condition === '-1'
                                        }
                                        style={{ display: 'inline' }}
                                        name={`condition${index === 0 ? '' : index + 1}`}
                                        value={opcao.condition || '-1'}
                                        onChange={e => handleChangeComboCondicao(e, index)}
                                    >
                                        <option value="-1" />
                                        {getCondicoes(index)}
                                    </Input>
                                    <div className="invalid-feedback">
                                        {t('CampoBuscaObrigatorio')}
                                    </div>
                                </Col>
                            ) : (
                                ''
                            )}
                        </Row>
                    ))}

                {permissionFilters === true ? (
                    <Row className="pt-3">
                        <Col
                            md={comparacao === 'entre' ? '9' : '7'}
                            xs="12"
                            className="w-100 additional_search_input pl-sm-0 pl-2 pr-sm-0 pr-2"
                        >
                            <Row>
                                <Col sm="3" xs="12">
                                    <label
                                        htmlFor="condicao"
                                        className="mt-2 mt-sm-0 mb-2 preto50"
                                    >
                                        {t('Condicao')}
                                    </label>
                                    <Input
                                        onChange={handleOnChangeCondition}
                                        name="comparacao"
                                        type="select"
                                        value={comparacao}
                                    >
                                        <option value="-1"></option>
                                        <option value="=">{t('IgualA')}</option>
                                        <option value=">">{t('MaiorQue')}</option>
                                        <option value="<">{t('MenorQue')}</option>
                                        <option value="entre">{t('Entre')}</option>
                                    </Input>
                                </Col>

                                <Col sm="4" xs="12">
                                    <label
                                        htmlFor="tipo_atos"
                                        className="mb-2 mt-2 mt-sm-0 preto50"
                                    >
                                        {t('AnoPublicacao')}
                                    </label>
                                    <Input
                                        value={dataInicial}
                                        onChange={e => setDataInicial(e.target.value)}
                                        disabled={!isValidComparacao(comparacao)}
                                        name="data_inicial"
                                        type="number"
                                    />
                                </Col>

                                {comparacao === 'entre' ? (
                                    <Col sm="4" xs="12">
                                        <label
                                            htmlFor="tipo_atos"
                                            className="mb-2 mt-2 mt-sm-0 preto50"
                                        >
                                            {t('AnoPublicacaoFinal')}
                                        </label>
                                        <Input
                                            value={dataFinal}
                                            onChange={e => setDataFinal(e.target.value)}
                                            name="data_final"
                                            type="number"
                                        />
                                    </Col>
                                ) : (
                                    ''
                                )}
                            </Row>
                        </Col>
                    </Row>
                ) : (
                    ''
                )}
            </Col>

            <Col
                md="2"
                xs="12"
                className={classnames('mt-3', { 'mb-4': props.error })}
            >
                <button
                    onClick={handleClickSearch}
                    title={t('Pesquisar')}
                    type="submit"
                    className="btn-generic--primeiro ml-0 btn-responsivo mt-md-3 mt-0"
                    disabled={props.loading}
                >
                    <FontAwesomeIcon icon="search" className="r5" />
                    <span>{t('Pesquisar')}</span>
                </button>
            </Col>
        </Row>
    );
};

export default PesquisaJurisprudencia;